import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  User as UserIcon,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  Trash2,
  Edit,
  AlertTriangle,
  Shield,
  Bike as BikeIcon,
  Ruler,
  Leaf,
  BarChart,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { userService, calculateUserCO2Offset } from "@/api/userService";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import UserRideHistory from "./components/UserRideHistory";
import UserTransactionHistory from "./components/UserTransactionHistory";

const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser, deleteUser } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  
  if (!userId) {
    React.useEffect(() => {
      console.error("No user ID provided in URL parameters");
      toast.error("No user ID provided. Redirecting to users list.");
      navigate("/users");
    }, [navigate]);
    return null;
  }
  
  const {
    data: user,
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: ["user", userId],
    queryFn: () => userService.getUser(userId),
    refetchOnWindowFocus: false,
    retry: 1,
  });
  
  React.useEffect(() => {
    if (error) {
      console.error(`Error fetching user ${userId}:`, error);
      if (isError) {
        toast.error(`Failed to load user details: ${error instanceof Error ? error.message : "Unknown error"}`);
      }
    }
  }, [error, userId, isError]);
  
  const handleDeleteUser = async () => {
    if (isDeleting) return;
    
    try {
      setIsDeleting(true);
      console.log("Attempting to delete user:", userId);
      
      if (!currentUser || currentUser.role !== "admin") {
        toast.error("Only administrators can delete users");
        setIsDeleting(false);
        return;
      }
      
      if (user?.role === "admin") {
        toast.error("Admin users cannot be deleted");
        setIsDeleting(false);
        return;
      }
      
      await deleteUser(userId);
      toast.success("User successfully deleted");
      
      // Use setTimeout to ensure state updates are processed before navigation
      setTimeout(() => {
        navigate("/users");
      }, 100);
    } catch (error) {
      console.error(`Error deleting user ${userId}:`, error);
      toast.error(`Failed to delete user: ${error instanceof Error ? error.message : "Unknown error"}`);
      setIsDeleting(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/users")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Users
          </Button>
        </div>
        
        <div className="space-y-2">
          <Skeleton className="h-10 w-1/3" />
          <Skeleton className="h-6 w-1/4" />
        </div>
        
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-6">
            <Skeleton className="h-[300px] w-full rounded-lg" />
          </div>
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-red-100 p-3">
          <AlertTriangle className="h-6 w-6 text-red-600" />
        </div>
        <h3 className="mt-4 text-lg font-medium">User Not Found</h3>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">
          We couldn't find a user with ID {userId}. They may have been removed or there was an error.
        </p>
        <Button className="mt-4" asChild>
          <Link to="/users">Return to Users List</Link>
        </Button>
      </div>
    );
  }
  
  const co2Offset = calculateUserCO2Offset(user.totalDistance);
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/users">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Users
              </Link>
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">{user.name}</h1>
            {user.role === "admin" && (
              <Badge className="bg-bike-primary">Admin</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild disabled={isDeleting}>
            <Link to={`/users/${userId}/edit`}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
          
          {user.role !== "admin" && currentUser?.role === "admin" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={isDeleting}>
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete the user
                    "{user.name}" from the system and remove all associated data.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    className="bg-red-500 hover:bg-red-600"
                    onClick={handleDeleteUser}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      "Delete"
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
      
      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="rides">Ride History</TabsTrigger>
          <TabsTrigger value="transactions">Carbon Credits</TabsTrigger>
        </TabsList>
        
        <TabsContent value="details" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>User Profile</CardTitle>
                <CardDescription>Personal information and account details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarFallback className="text-lg">
                      {user.name.split(" ").map(n => n[0]).join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-lg font-medium">{user.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {user.role === "admin" ? "Administrator" : "Standard User"}
                    </p>
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-3">
                  {user.organizationName && (
                    <div className="flex items-start gap-2">
                      <Shield className="mt-0.5 h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Organization</p>
                        <p className="text-sm">{user.organizationName}</p>
                      </div>
                    </div>
                  )}
                  
                  {user.address && (
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Address</p>
                        <p className="text-sm">{user.address}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start gap-2">
                    <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Account Created</p>
                      <p className="text-sm">
                        {user.createdAt && !isNaN(new Date(user.createdAt).getTime())
                          ? new Date(user.createdAt).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Quick Statistics</CardTitle>
                <CardDescription>Usage statistics for this user</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <BikeIcon className="h-4 w-4" />
                      <span>Total Rides</span>
                    </div>
                    <div className="mt-2 text-3xl font-bold">{user.totalRides}</div>
                  </div>
                  
                  <div className="rounded-lg border p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Ruler className="h-4 w-4" />
                      <span>Total Distance</span>
                    </div>
                    <div className="mt-2 text-3xl font-bold">{user.totalDistance.toFixed(1)} km</div>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Ruler className="h-4 w-4" />
                      <span>Offset Distance</span>
                    </div>
                    <div className="mt-2 text-2xl font-bold">{user.totalOffsetDistance?.toFixed(1) || "0"} km</div>
                    <div className="mt-1 text-sm text-muted-foreground">Distance counted for offset</div>
                  </div>

                  <div className="rounded-lg border p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Ruler className="h-4 w-4" />
                      <span>Non-Offset Distance</span>
                    </div>
                    <div className="mt-2 text-2xl font-bold">{user.totalNonOffsetDistance?.toFixed(1) || "0"} km</div>
                    <div className="mt-1 text-sm text-muted-foreground">Distance not counted for offset</div>
                  </div>
                </div>

                <div className="rounded-lg border bg-green-50 p-4">
                  <div className="flex items-center gap-2 text-sm text-green-800">
                    <Leaf className="h-4 w-4" />
                    <span>CO2 Saved</span>
                  </div>
                  <div className="mt-2 text-3xl font-bold text-green-800">
                    {user.totalCO2Offset?.toFixed(2) || "0"} kg
                  </div>
                  <div className="mt-1 text-sm text-green-600">
                    Based on UK DEFRA conversion factors (0.16272 kg CO2e per km)
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="rides">
          <UserRideHistory userId={userId} />
        </TabsContent>
        
        <TabsContent value="transactions">
          <UserTransactionHistory userId={userId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UserDetailPage;
